/**
 * 63.5 cover by material, the soil's wear seen (lane E; lane A's `state.groundWear`, core/groundWear.ts): soil a cell of which
 * has taken GROUND_WEAR.hitsToPass rounds passes them as sand. Each worn cell is drawn as chewed earth — a patch of the dig's
 * turned spoil (rendering/digLook.ts DIG_LOOK.soil, the lip's colour) about the size of a dig's lip, darkening and spreading
 * with its count from `WEAR_LOOK.show` rounds to worn through, with clods thrown about it toward the end — so a crest that
 * has been chewed through reads as broken ground before a round comes through it. Read from the state like the digs: rebuilt
 * when the wear array changes, at most every `rebuildS`. Two instanced draws for every worn cell on the planet. Presentation
 * only.
 */
import * as THREE from 'three';
import type { GameState, PlanetState, Vec3 } from '../types';
import { GROUND_WEAR } from '../constants/ground';
import { wearCellDir } from '../core/groundWear';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { DIG_LOOK, UNDUG_GROUND } from './digLook';
import { markShared } from './meshBake';

export const WEAR_LOOK = {
  /** rounds before a cell shows; it is worn through at GROUND_WEAR.hitsToPass */
  show: 3,
  /** the patch's radius (people) at its first showing and worn through */
  radius: [0.25, 0.55] as const,
  /** clods about a cell from this share of the way to worn through, `clods` of them at worn through */
  clodsFrom: 0.75, clods: 4,
  /** a lift off the drawn ground (people), and the most often the patches are rebuilt (seconds) */
  lift: 0.015, rebuildS: 0.25,
};

const SPOIL = new THREE.Color(DIG_LOOK.soil!.spoil), GROUND = new THREE.Color(UNDUG_GROUND.soil), CLOD = new THREE.Color(DIG_LOOK.soil!.walls);
const hash = (id: number, salt: number) => { const v = Math.sin(id * 12.9898 + salt * 78.233) * 43758.5453; return v - Math.floor(v); };

/** An irregular flat patch, radius ~1, in the xz plane (its up +y). */
function patchGeometry(): THREE.BufferGeometry {
  const n = 12, pos: number[] = [0, 0, 0];
  for (let i = 0; i <= n; i++) { const a = (i % n) / n * Math.PI * 2, r = 0.78 + 0.22 * hash(i, 3); pos.push(Math.cos(a) * r, 0, Math.sin(a) * r); }
  const idx: number[] = [];
  for (let i = 1; i <= n; i++) idx.push(0, i + 1, i);
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx); g.computeVertexNormals();
  return g;
}

export class GroundWearView {
  private patches: THREE.InstancedMesh | null = null;
  private clods: THREE.InstancedMesh | null = null;
  private seen: readonly number[] | undefined = undefined;
  /** each cell's place on the drawn ground (found once; the planets changing — a crater, a dig — finds them again) */
  private readonly placed = new Map<number, { o: THREE.Vector3; up: THREE.Vector3; q: THREE.Quaternion; t1: THREE.Vector3; t2: THREE.Vector3 }>();
  private placedFor: readonly PlanetState[] | null = null;
  /** each cell's instances as last built, by the count they were built for (a cell whose count holds is copied, not rebuilt) */
  private readonly built = new Map<number, { count: number; patch: Float32Array; colour: Float32Array; clods: Float32Array; clodColours: Float32Array }>();
  private wait = 0;
  /** the cells drawn at the last rebuild (for tests and the cost line) */
  shown = 0;
  private readonly patchGeo = patchGeometry();
  private readonly clodGeo = new THREE.DodecahedronGeometry(1, 0);
  private readonly patchMat = markShared(new THREE.MeshLambertMaterial({ color: '#ffffff', polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
  private readonly clodMat = markShared(new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true }));

  constructor(private readonly scene: THREE.Scene) {}

  /**
   * Each frame: rebuild when the wear changed (at most every WEAR_LOOK.rebuildS). `groundAt` is the drawn ground's radius
   * under a direction (the host's: the fine relief and the digs).
   */
  update(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'groundWear'>>, dt: number, groundAt: (planet: PlanetState, dir: Vec3) => number): void {
    this.wait -= dt;
    if (state.groundWear === this.seen || this.wait > 0) return;
    this.seen = state.groundWear; this.wait = WEAR_LOOK.rebuildS;
    this.rebuild(state, groundAt);
  }

  private rebuild(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'groundWear'>>, groundAt: (planet: PlanetState, dir: Vec3) => number): void {
    const wear = state.groundWear ?? [], person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k, L = WEAR_LOOK;
    const cells: Array<{ id: number; count: number; k: number }> = [];
    for (let i = 0; i < wear.length; i += 2) if (wear[i + 1] >= L.show) cells.push({ id: wear[i], count: wear[i + 1], k: Math.min(1, (wear[i + 1] - L.show) / Math.max(1, GROUND_WEAR.hitsToPass - L.show)) });
    const clodN = cells.reduce((a, c) => a + (c.k >= L.clodsFrom ? Math.round(L.clods * c.k) : 0), 0);
    this.patches = this.ensure(this.patches, this.patchGeo, this.patchMat, cells.length, 'groundWear:patches');
    this.clods = this.ensure(this.clods, this.clodGeo, this.clodMat, clodN, 'groundWear:clods');
    const m = new THREE.Matrix4(), s = new THREE.Vector3(), p = new THREE.Vector3(), col = new THREE.Color(), Y = new THREE.Vector3(0, 1, 0);
    if (this.placedFor !== state.planets) { this.placed.clear(); this.built.clear(); this.placedFor = state.planets; }
    const pm = this.patches.instanceMatrix.array as Float32Array, pc = this.patches.instanceColor!.array as Float32Array;
    const cm = this.clods.instanceMatrix.array as Float32Array, cc = this.clods.instanceColor!.array as Float32Array;
    let ci = 0, n = 0;
    for (const c of cells) {
      const was = this.built.get(c.id);
      if (was && was.count === c.count) {   // (its count held: its instances as they were)
        pm.set(was.patch, n * 16); pc.set(was.colour, n * 3); cm.set(was.clods, ci * 16); cc.set(was.clodColours, ci * 3);
        n++; ci += was.clods.length / 16;
        continue;
      }
      let pl = this.placed.get(c.id);
      if (!pl) {
        const { planetIndex, dir } = wearCellDir(state, c.id), planet = state.planets[planetIndex];
        if (!planet) continue;
        // on the drawn ground, tilted to it (three samples round the cell: the ground's normal there), in the world
        const at = (d: Vec3) => { const r = groundAt(planet, d); return new THREE.Vector3(planet.position.x + d.x * r, planet.position.y + d.y * r, planet.position.z + d.z * r); };
        const n0 = new THREE.Vector3(dir.x, dir.y, dir.z), t1 = new THREE.Vector3().copy(Math.abs(n0.y) < 0.9 ? Y : new THREE.Vector3(1, 0, 0)).cross(n0).normalize(), t2 = new THREE.Vector3().crossVectors(n0, t1);
        const e = (0.5 * person) / planet.radius;
        const o = at(dir), a = at(n0.clone().addScaledVector(t1, e).normalize()), b = at(n0.clone().addScaledVector(t2, e).normalize());
        const up = new THREE.Vector3().crossVectors(b.clone().sub(o), a.clone().sub(o)).normalize();
        if (up.dot(n0) < 0) up.negate();
        const q = new THREE.Quaternion().setFromUnitVectors(Y, up).multiply(new THREE.Quaternion().setFromAxisAngle(Y, hash(c.id, 1) * Math.PI * 2));
        pl = { o, up, q, t1, t2 };
        this.placed.set(c.id, pl);
      }
      const { o, up, q, t1, t2 } = pl;
      const r = (L.radius[0] + (L.radius[1] - L.radius[0]) * c.k) * person;
      p.copy(o).addScaledVector(up, L.lift * person);
      m.compose(p, q, s.set(r, 1, r * (0.75 + 0.25 * hash(c.id, 2))));
      this.patches!.setMatrixAt(n, m);
      this.patches!.setColorAt(n, col.copy(GROUND).lerp(SPOIL, 0.45 + 0.55 * c.k));
      const n0 = n++, c0 = ci;
      // the clods, thrown about it from halfway
      const k = c.k >= L.clodsFrom ? Math.round(L.clods * c.k) : 0;
      for (let j = 0; j < k; j++, ci++) {
        const ang = hash(c.id, 10 + j) * Math.PI * 2, d = r * (0.6 + 0.6 * hash(c.id, 20 + j)), sz = person * (0.035 + 0.035 * hash(c.id, 30 + j));
        const cp = p.copy(o).addScaledVector(t1, Math.cos(ang) * d).addScaledVector(t2, Math.sin(ang) * d).addScaledVector(up, sz * 0.5);
        m.compose(cp, q, s.set(sz, sz * 0.7, sz));
        this.clods!.setMatrixAt(ci, m);
        this.clods!.setColorAt(ci, col.copy(CLOD).lerp(SPOIL, hash(c.id, 40 + j)));
      }
      this.built.set(c.id, { count: c.count, patch: pm.slice(n0 * 16, n0 * 16 + 16), colour: pc.slice(n0 * 3, n0 * 3 + 3), clods: cm.slice(c0 * 16, ci * 16), clodColours: cc.slice(c0 * 3, ci * 3) });
    }
    for (const mesh of [this.patches, this.clods]) { mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true; }
    this.patches.count = n; this.clods.count = ci;
    this.shown = n;
  }

  /** An instanced mesh with room for `n` (grown in steps, never shrunk), in the scene. */
  private ensure(mesh: THREE.InstancedMesh | null, geo: THREE.BufferGeometry, mat: THREE.Material, n: number, name: string): THREE.InstancedMesh {
    if (mesh && mesh.instanceMatrix.count >= n) return mesh;
    if (mesh) { this.scene.remove(mesh); mesh.dispose(); }
    const made = new THREE.InstancedMesh(geo, mat, Math.max(32, Math.ceil(n * 1.5)));
    made.name = name;   // (the renderer census names every instanced layer)
    made.frustumCulled = false; made.count = 0;
    made.setColorAt(0, new THREE.Color('#ffffff'));
    this.scene.add(made);
    return made;
  }

  dispose(): void {
    for (const mesh of [this.patches, this.clods]) if (mesh) { this.scene.remove(mesh); mesh.dispose(); }
    this.patches = this.clods = null;
    this.patchGeo.dispose(); this.clodGeo.dispose();
  }
}
